import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of, race } from 'rxjs';
import { catchError, concatMap, defaultIfEmpty, map, take, tap, withLatestFrom } from 'rxjs/operators';
import { ChangeLogEntry, WoundStatus } from '@mona/models';
import {
    applyInstancesChanges,
    ChangeLogAction,
    ChangeLogSelectors,
    getPersistedChangesData,
} from '@mona/pdms/data-access-changelog';
import {
    applyLastChangedByToPrescriptions,
    withCurrentEncounterId,
    currentEncounterId,
    isEmptyEncounterId,
} from '@mona/pdms/data-access-combined';
import { PractitionersFacade } from '@mona/pdms/data-access-practitioners';
import { isSameEncounterFilter } from '@mona/shared/utils';
import { WoundStatusApi } from '../../infrastructure';
import { WoundStatusAction } from '../actions';
import { selectWoundAll } from '../selectors';

/**
 * WoundStatus Effects
 */
@Injectable()
export class WoundStatusEffects {
    changeLogMap$ = this.store.select(ChangeLogSelectors.getChangesMap);
    selectWoundAll$ = this.store.select(selectWoundAll);

    /** Load Wound status */
    loadWoundStatus$ = createEffect(() =>
        this.actions$.pipe(
            ofType(WoundStatusAction.loadWoundStatus),
            withCurrentEncounterId(),
            concatMap(([, encounterId]) =>
                race(
                    isEmptyEncounterId(this.store).pipe(
                        take(1),
                        map(() => WoundStatusAction.loadWoundStatusCancelled({ reason: 'encounter-cleared' })),
                    ),
                    this.woundStatusApi.getWoundStatus(encounterId).pipe(
                        isSameEncounterFilter(currentEncounterId(this.store), encounterId),
                        withLatestFrom(this.changeLogMap$, this.practitionersFacade.practitionersMap$),
                        map(([data, changesMap, practitionersMap]) => {
                            const changes = changesMap['WoundStatus'] || [];

                            if (changes.length) {
                                data = applyInstancesChanges(data, changes) as WoundStatus[];
                            }

                            data = applyLastChangedByToPrescriptions(data, practitionersMap);

                            return WoundStatusAction.loadWoundStatusSuccess({ data });
                        }),
                        catchError(error => of(WoundStatusAction.loadWoundStatusFailure({ error }))),
                        defaultIfEmpty(
                            WoundStatusAction.loadWoundStatusCancelled({ reason: 'encounter-changed' }) as any,
                        ),
                    ),
                ),
            ),
        ),
    );

    /** Listen change save success & upsert entity + show message */
    onChangeSaved$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(
                    ChangeLogAction.saveChangeAction.succeededAction,
                    ChangeLogAction.saveChangesAction.succeededAction,
                ),
                withLatestFrom(this.selectWoundAll$, this.changeLogMap$),
                tap(([, data, changesMap]) => {
                    const changes: ChangeLogEntry<WoundStatus>[] = changesMap['WoundStatus'] || [];

                    if (changes.length) {
                        const wounds = applyInstancesChanges(data, changes);

                        this.store.dispatch(WoundStatusAction.upsertWoundStatus({ data: wounds }));
                    }
                }),
            ),
        { dispatch: false },
    );

    /** Listen change persist success & add one - realistic update */
    onChangePersisted$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(ChangeLogAction.persistChangesAction.succeededAction),
                withLatestFrom(this.selectWoundAll$, this.changeLogMap$, this.practitionersFacade.practitionersMap$),
                tap(([, data, changesMap, practitionersMap]) => {
                    const changes = changesMap['WoundStatus'] || [];

                    if (changes.length) {
                        const removeUpdateData = getPersistedChangesData(data, changes);
                        removeUpdateData.toUpdate = applyLastChangedByToPrescriptions(
                            removeUpdateData.toUpdate,
                            practitionersMap,
                        );

                        this.store.dispatch(
                            WoundStatusAction.addWoundStatusChanges({
                                toUpdateEntities: removeUpdateData.toUpdate || [],
                                toRemoveIds: removeUpdateData.toRemove.map(({ id }) => id) || [],
                            }),
                        );
                    }
                }),
            ),
        { dispatch: false },
    );

    /**
     * Constructor
     *
     * @param store
     * @param actions$
     * @param woundStatusApi
     * @param practitionersFacade
     */
    constructor(
        private store: Store<any>,
        private actions$: Actions,
        private woundStatusApi: WoundStatusApi,
        private practitionersFacade: PractitionersFacade,
    ) {}
}
